/**
 * useOptimisticUpdate - Hook for implementing optimistic UI updates
 * Shows updated values immediately and reverts on error
 */

import { useState, useCallback, useRef } from 'react';

interface OptimisticUpdateOptions<T> {
  onSuccess?: (data: T) => void;
  onError?: (error: Error, previousData: T) => void;
}

export function useOptimisticUpdate<T>(
  initialData: T,
  options?: OptimisticUpdateOptions<T>
) {
  const [data, setData] = useState<T>(initialData);
  const [isUpdating, setIsUpdating] = useState(false);
  const previousDataRef = useRef<T>(initialData);

  const optimisticUpdate = useCallback(
    async (
      newData: Partial<T>,
      updateFn: () => Promise<void>
    ): Promise<void> => {
      // Store current data for potential rollback
      previousDataRef.current = data;

      // Optimistically update the UI
      setData(prevData => ({ ...prevData, ...newData }));
      setIsUpdating(true);

      try {
        // Perform the actual update
        await updateFn();

        // Success - keep the optimistic update
        options?.onSuccess?.(data);
      } catch (error) {
        // Error - rollback to previous data
        setData(previousDataRef.current);

        // Call error handler
        options?.onError?.(
          error instanceof Error ? error : new Error('Update failed'),
          previousDataRef.current
        );

        // Re-throw to allow component-level error handling
        throw error;
      } finally {
        setIsUpdating(false);
      }
    },
    [data, options]
  );

  const reset = useCallback(() => {
    setData(initialData);
    previousDataRef.current = initialData;
  }, [initialData]);

  const updateData = useCallback((newData: T) => {
    setData(newData);
    previousDataRef.current = newData;
  }, []);

  return {
    data,
    isUpdating,
    optimisticUpdate,
    reset,
    updateData,
  };
}
